import type { MotionKind } from './furnitureMotion';

/**
 * ВОЛНА W3 — «ПРЯМАЯ МАНИПУЛЯЦИЯ ФАСАДАМИ» (PHAETON-PREMIUM, вау-топ-3).
 *
 * Семантика движений НЕ придумывается заново: ею владеет контракт
 * `domain.motionContract` через `FurnitureMotionObject` (furnitureMotion.ts,
 * kind door|drawer, openMagnitude, оси, направление). Этот модуль — только
 * ЖЕСТ: как палец/мышь ведут прогресс 0..1 этого контракта и что происходит
 * при отпускании.
 *
 * Правила жеста (solid-режим):
 *   pointerdown на фасаде → порог FACADE_DRAG_THRESHOLD_PX (до порога —
 *   обычный клик: выбор, кольцо, двойной клик в ателье работают как были);
 *   за порогом драг ведёт ПРОГРЕСС вдоль оси открывания, спроецированной на
 *   экран: купе едет по рельсе (clamp 0..сдвиг), распашная поворачивается
 *   за край до FACADE_DOOR_DRAG_MAX_DEG, ящик выдвигается;
 *   отпускание у конца — «доводчик» easeOutQuint ~450 мс, у начала — мягкое
 *   закрытие; сильный бросок — инерция с затуханием; Esc — откат к позе
 *   на момент захвата.
 *
 * Позу фасада в кадре пишет СУЩЕСТВУЮЩИЙ аниматор сцены (`useAnimations`):
 * этот модуль держит реестр переопределений `gestureProgress`, который
 * аниматор читает раньше стора и применяет 1:1 (без демпфера). Поэтому
 * второй системы holders здесь нет и не должно быть.
 *
 * ЗВУК — не наша волна (W2): подключается одним слушателем
 * `setFacadeMotionEventListener((kind, phase, info) => …)`.
 */

export const FACADE_DRAG_THRESHOLD_PX = 4;
/** Меньше этого экранного хода у оси открывания — фасад не «схватить» (вид с торца). */
export const FACADE_DRAG_MIN_TRAVEL_PX = 24;
/** Драг обязан смотреть вдоль оси открывания (cos ≥ 0.5), иначе жест уступает орбите камеры. */
export const FACADE_DRAG_ALIGNMENT_COS = 0.5;
/** Распашную за край тащим не дальше этого угла (контракт может позволять больше). */
export const FACADE_DOOR_DRAG_MAX_DEG = 100;
/** «Доводчик» до полностью открытого. */
export const FACADE_SETTLE_OPEN_MS = 450;
/** Мягкое закрытие у начала. */
export const FACADE_SETTLE_CLOSE_MS = 520;
/** Возврат при Esc/отмене. */
export const FACADE_CANCEL_RETURN_MS = 380;
/** Бросок: |скорость| прогресса/с выше этого — инерция решает направление. */
export const FACADE_FLING_VELOCITY_PER_S = 0.9;
/** Окно оценки скорости броска, мс. */
export const FACADE_VELOCITY_WINDOW_MS = 140;

export type FacadeMotionPhase =
  | 'start'          // жест захватил фасад
  | 'drag'           // палец ведёт (см. FACADE_DRAG_EVENT_MIN_MS)
  | 'fling'          // бросок с инерцией
  | 'settle-open'    // доводчик до открытого
  | 'settle-close'   // мягкое закрытие
  | 'cancel'         // Esc/сбой — откат к позе захвата
  | 'settle-end';    // доводчик/закрытие доехало

export type FacadeMotionEventInfo = {
  entityId: string;
  sliding: boolean;
  progress: number;
};

/** Экспорт-хук для волны W2 (звук колёсика купе, доводчика, тихий клик). */
export type FacadeMotionEventListener = (
  kind: MotionKind,
  phase: FacadeMotionPhase,
  info: FacadeMotionEventInfo,
) => void;

let motionEventListener: FacadeMotionEventListener | null = null;

export function setFacadeMotionEventListener(listener: FacadeMotionEventListener | null): void {
  motionEventListener = listener;
}

function emitFacadeMotion(
  kind: MotionKind,
  phase: FacadeMotionPhase,
  info: FacadeMotionEventInfo,
): void {
  try {
    motionEventListener?.(kind, phase, info);
  } catch {
    // Звук не должен ронять жест.
  }
}

/** Тихий доступ к слушателю (тестам). */
export function facadeMotionEventListenerForTest(): FacadeMotionEventListener | null {
  return motionEventListener;
}

// ——— арифметика жеста ———

export function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

export function easeOutQuint(t: number): number {
  const c = 1 - clamp01(t);
  return 1 - c * c * c * c * c;
}

export function easeInOutCubic(t: number): number {
  const x = clamp01(t);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

/**
 * Порог взятия фасада. Правило то же, что у выбора сцены
 * (useSelection: |dx| > 4 || |dy| > 4): как только жест взял фасад, выбор
 * уже считает это перетаскиванием и НЕ кликает — клик и драг не спорят.
 */
export function facadeDragExceedsThreshold(dxPx: number, dyPx: number): boolean {
  return Math.abs(dxPx) > FACADE_DRAG_THRESHOLD_PX || Math.abs(dyPx) > FACADE_DRAG_THRESHOLD_PX;
}

/** Ось открывания на экране: единичный вектор + полный ход в пикселях. */
export type FacadeDragAxis = {
  dirX: number;
  dirY: number;
  travelPx: number;
};

/** Смотрит ли драг вдоль оси открывания (иначе жест не забирает указатель у орбиты). */
export function facadeDragAligned(
  dxPx: number,
  dyPx: number,
  axis: FacadeDragAxis,
  minCos: number = FACADE_DRAG_ALIGNMENT_COS,
): boolean {
  if (axis.travelPx < 1) return false;
  const len = Math.hypot(dxPx, dyPx);
  if (len < 1) return false;
  const cos = (dxPx * axis.dirX + dyPx * axis.dirY) / len;
  return Math.abs(cos) >= minCos;
}

/**
 * Прогресс фасада по экранному драгу: проекция смещения на ось открывания,
 * нормированная полным ходом, от стартовой позы. Кламп 0..maxProgress
 * (у распашной maxProgress < 1, когда контракт разрешает > 100°).
 */
export function facadeDragProgress(
  startProgress: number,
  axis: FacadeDragAxis,
  dxPx: number,
  dyPx: number,
  maxProgress = 1,
): number {
  if (axis.travelPx < 1) return startProgress;
  const along = dxPx * axis.dirX + dyPx * axis.dirY;
  const raw = startProgress + along / axis.travelPx;
  return Math.min(maxProgress, clamp01(raw));
}

/** Распашную тащим до 100°, даже если контракт открывает шире. */
export function facadeDoorDragMagnitude(contractOpenRad: number): number {
  const maxRad = (FACADE_DOOR_DRAG_MAX_DEG * Math.PI) / 180;
  return Math.max(0, Math.min(contractOpenRad, maxRad));
}

// ——— скорость броска ———

/** Скорость прогресса по последним сэмплам окна (прогресс/с). */
export class FacadeVelocityTracker {
  private readonly samples: Array<{ tMs: number; progress: number }> = [];

  push(nowMs: number, progress: number): void {
    this.samples.push({ tMs: nowMs, progress });
    const cutoff = nowMs - FACADE_VELOCITY_WINDOW_MS;
    while (this.samples.length > 2 && this.samples[0].tMs < cutoff) this.samples.shift();
  }

  velocityPerSecond(nowMs: number): number {
    if (this.samples.length < 2) return 0;
    const first = this.samples[0];
    const last = this.samples[this.samples.length - 1];
    // Пауза длиннее окна гасит бросок: держать фасад неподвижно и отпустить —
    // это не «сильно толкнуть».
    if (nowMs - last.tMs > FACADE_VELOCITY_WINDOW_MS) return 0;
    const dtMs = Math.max(0, Math.min(last.tMs, nowMs) - first.tMs);
    if (dtMs < 16) return 0;
    return ((last.progress - first.progress) * 1000) / dtMs;
  }

  reset(): void {
    this.samples.length = 0;
  }
}

// ——— план доводчика ———

export type FacadeSettlePlan = {
  from: number;
  /** Доводчик едет к концу (0|1); отмена — к позе на момент захвата. */
  to: number;
  durationMs: number;
  easing: 'easeOutQuint' | 'easeInOutCubic';
  phase: 'fling' | 'settle-open' | 'settle-close' | 'cancel';
};

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

/**
 * Что делать при отпускании: сильный бросок (|v| ≥ FACADE_FLING_VELOCITY_PER_S)
 * летит по инерции в свою сторону; иначе ближайший конец — у конца
 * «доводчик» easeOutQuint 450 мс, у начала мягкое закрытие.
 */
export function facadeSettlePlan(
  progress: number,
  velocityPerSecond: number,
): FacadeSettlePlan {
  const from = clamp01(progress);
  const speed = Math.abs(velocityPerSecond);
  if (speed >= FACADE_FLING_VELOCITY_PER_S) {
    const to: 0 | 1 = velocityPerSecond > 0 ? 1 : 0;
    // Длительность броска — от остатка пути и скорости, в разумных рамках.
    const distance = Math.abs(to - from);
    const durationMs = clamp(Math.round((distance / speed) * 1000 * 1.15), 280, 620);
    return { from, to, durationMs, easing: 'easeOutQuint', phase: 'fling' };
  }
  if (from >= 0.5) {
    return {
      from,
      to: 1,
      durationMs: FACADE_SETTLE_OPEN_MS,
      easing: 'easeOutQuint',
      phase: 'settle-open',
    };
  }
  return {
    from,
    to: 0,
    durationMs: FACADE_SETTLE_CLOSE_MS,
    easing: 'easeInOutCubic',
    phase: 'settle-close',
  };
}

/** Аниматор доводчика/закрытия с инъективными часами (тесты гоняют время вручную). */
export class FacadeSettleDriver {
  readonly entityId: string;
  readonly kind: MotionKind;
  readonly sliding: boolean;
  readonly plan: FacadeSettlePlan;
  private readonly startedMs: number;
  private finished = false;

  constructor(init: {
    entityId: string;
    kind: MotionKind;
    sliding: boolean;
    plan: FacadeSettlePlan;
    startedMs: number;
  }) {
    this.entityId = init.entityId;
    this.kind = init.kind;
    this.sliding = init.sliding;
    this.plan = init.plan;
    this.startedMs = init.startedMs;
  }

  value(nowMs: number): number {
    if (this.finished) return this.plan.to;
    const t = this.plan.durationMs <= 0
      ? 1
      : clamp01((nowMs - this.startedMs) / this.plan.durationMs);
    const eased = this.plan.easing === 'easeOutQuint' ? easeOutQuint(t) : easeInOutCubic(t);
    if (t >= 1) this.finished = true;
    return this.plan.from + (this.plan.to - this.plan.from) * eased;
  }

  done(nowMs: number): boolean {
    this.value(nowMs);
    return this.finished;
  }
}

// ——— реестр переопределений позы (читает useAnimations раньше стора) ———

type GestureMeta = { kind: MotionKind; sliding: boolean; startProgress: number };

const gestureProgressById = new Map<string, number>();
const settleDriversById = new Map<string, FacadeSettleDriver>();
const gestureMetaById = new Map<string, GestureMeta>();
const activeDragEntityIds = new Set<string>();
const lastDragEventAt = new Map<string, number>();

/**
 * Прогресс фасада под прямым управлением (жест/инерция/доводчик) или null,
 * когда позой владеет стор openedPanels. Читается аниматором сцены каждый
 * кадр; меняется только здесь.
 */
export function facadeGestureProgressOf(entityId: string): number | null {
  return gestureProgressById.has(entityId)
    ? gestureProgressById.get(entityId) as number
    : null;
}

/** Жив ли какой-либо жест/доводчик — рендер-цикл будит pointermove только тогда. */
export function isFacadeGestureAnimating(): boolean {
  return activeDragEntityIds.size > 0 || settleDriversById.size > 0;
}

export function beginFacadeGesture(
  entityId: string,
  startProgress: number,
  meta: { kind: MotionKind; sliding: boolean },
): void {
  activeDragEntityIds.add(entityId);
  settleDriversById.delete(entityId);
  gestureProgressById.set(entityId, clamp01(startProgress));
  gestureMetaById.set(entityId, { ...meta, startProgress: clamp01(startProgress) });
  emitFacadeMotion(meta.kind, 'start', { entityId, sliding: meta.sliding, progress: clamp01(startProgress) });
}

export function updateFacadeGesture(
  entityId: string,
  progress: number,
  meta: { kind: MotionKind; sliding: boolean },
  minEventIntervalMs = 80,
): void {
  const value = clamp01(progress);
  gestureProgressById.set(entityId, value);
  const now = nowMs();
  const last = lastDragEventAt.get(entityId) ?? -Infinity;
  if (now - last >= minEventIntervalMs) {
    lastDragEventAt.set(entityId, now);
    emitFacadeMotion(meta.kind, 'drag', { entityId, sliding: meta.sliding, progress: value });
  }
}

/**
 * Отпускание: ставит доводчик/инерцию и возвращает управление позой ему.
 * Вызывающий узнаёт о финале колбэком tickFacadeSettles.
 */
export function settleFacadeGesture(
  entityId: string,
  plan: FacadeSettlePlan,
  meta: { kind: MotionKind; sliding: boolean },
  startedMs: number = nowMs(),
): FacadeSettleDriver {
  activeDragEntityIds.delete(entityId);
  const driver = new FacadeSettleDriver({ entityId, ...meta, plan, startedMs });
  settleDriversById.set(entityId, driver);
  emitFacadeMotion(meta.kind, plan.phase, {
    entityId,
    sliding: meta.sliding,
    progress: plan.from,
  });
  return driver;
}

/** Приватная установка водителя отката к позе захвата. */
function installCancelDriver(entityId: string, currentProgress: number): void {
  const meta = gestureMetaById.get(entityId);
  if (!meta) return;
  const plan: FacadeSettlePlan = {
    from: clamp01(currentProgress),
    to: meta.startProgress,
    durationMs: FACADE_CANCEL_RETURN_MS,
    easing: 'easeInOutCubic',
    phase: 'cancel',
  };
  activeDragEntityIds.delete(entityId);
  settleDriversById.set(
    entityId,
    new FacadeSettleDriver({ entityId, kind: meta.kind, sliding: meta.sliding, plan, startedMs: nowMs() }),
  );
  emitFacadeMotion(meta.kind, 'cancel', {
    entityId,
    sliding: meta.sliding,
    progress: clamp01(currentProgress),
  });
}

/**
 * Esc/сбой: откат ЖИВОГО движения (драга или уже едущего доводчика) к позе
 * на момент захвата. Возвращает число прерванных фасадов.
 */
export function cancelActiveFacadeMotion(): number {
  let cancelled = 0;
  const ids = new Set<string>([...activeDragEntityIds, ...settleDriversById.keys()]);
  for (const entityId of ids) {
    const current = gestureProgressById.get(entityId);
    if (current === undefined) continue;
    installCancelDriver(entityId, current);
    cancelled += 1;
  }
  return cancelled;
}

/** Финал движения: мета жеста больше не нужна (позой снова владеет стор). */
export function forgetFacadeGesture(entityId: string): void {
  gestureMetaById.delete(entityId);
  lastDragEventAt.delete(entityId);
}

/** Тихий сброс всего реестра (тесты/размонтирование сцены). */
export function resetFacadeGestureState(): void {
  gestureProgressById.clear();
  settleDriversById.clear();
  activeDragEntityIds.clear();
  gestureMetaById.clear();
  lastDragEventAt.clear();
}

/**
 * Такт доводчиков: обновляет переопределения поз и доигрывает их до конца.
 * Возвращает true, пока хоть один водитель жив (рендер-цикл остаётся горячим).
 * `onFinished` вызывается один раз на доехавший фасад.
 */
export function tickFacadeSettles(
  now: number,
  onFinished: (entityId: string, driver: FacadeSettleDriver) => void,
): boolean {
  if (!settleDriversById.size) return false;
  let anyAlive = false;
  for (const [entityId, driver] of [...settleDriversById]) {
    const value = driver.value(now);
    gestureProgressById.set(entityId, value);
    if (driver.done(now)) {
      settleDriversById.delete(entityId);
      gestureProgressById.delete(entityId);
      forgetFacadeGesture(entityId);
      emitFacadeMotion(driver.kind, 'settle-end', {
        entityId,
        sliding: driver.sliding,
        progress: driver.plan.to,
      });
      onFinished(entityId, driver);
    } else {
      anyAlive = true;
    }
  }
  return anyAlive;
}

// ——— часы (инъекция для тестов) ———

let clock: () => number = () => performance.now();

export function nowMs(): number {
  return clock();
}

/** Тестовый доступ: подставные часы для таймингов доводчика без rAF. */
export function setFacadeClockForTest(clockFn: (() => number) | null): void {
  clock = clockFn ?? (() => performance.now());
}
